import React, { useEffect, useMemo, useState } from 'react';

import {
  ArrowLeftOutlined,
  CalendarOutlined,
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  FilterOutlined,
  LockOutlined,
  MoreOutlined,
  NotificationOutlined,
  PlusOutlined,
  TeamOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import {
  App,
  Badge,
  Breadcrumb,
  Button,
  Card,
  Checkbox,
  Col,
  Dropdown,
  MenuProps,
  Popover,
  Row,
  Skeleton,
  Space,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import Search from 'antd/es/input/Search';
import Column from 'antd/es/table/Column';
import { SearchProps } from 'antd/lib/input';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ApiErrorData } from 'models/ApiResponse';
import { AdminUserDetail, ProfileStatusEntry } from 'models/Lead';
import {
  AcceptLeadsResponse,
  Association,
  AssociationStatus,
  AssociationsResponse,
  RejectLeadsResponse,
  RemoveLeadsResponse,
  RequirementDetail as RequirementDetailModel,
  RequirementStatus,
} from 'models/Requirement';

import { LeadService } from 'services/LeadService';
import { RequirementService } from 'services/RequirementService';

import { FunctionsHelper } from 'utils/FunctionsHelper';

import Layout from '../../../components/Layout/Layout';
import appI18n from '../../../config/i18n';
import DetailLeadModal from '../../Leads/Components/DetailLeadModal';
import CreateRequirementModal from '../Components/CreateRequirementModal';
import AssociateLeadsModal from './Components/AssociateLeadsModal';
import CapacityAlert from './Components/CapacityAlert';

import './RequirementDetail.scss';

const STATUS_COLOR: Record<RequirementStatus, string> = {
  open: 'green',
  closed: 'default',
};

const ASSOCIATION_STATUS_COLOR: Record<AssociationStatus, string> = {
  pending_review: 'gold',
  accepted: 'green',
  rejected: 'red',
};

const APTITUDE_COLOR: Record<'A' | 'B' | 'C', string> = {
  A: 'green',
  B: 'gold',
  C: 'red',
};

const AVATAR_GRADIENTS = [
  'linear-gradient(135deg, #1677FF, #4096FF)',
  'linear-gradient(135deg, #722ED1, #B37FEB)',
  'linear-gradient(135deg, #13C2C2, #36CFC9)',
  'linear-gradient(135deg, #FA8C16, #FFA940)',
  'linear-gradient(135deg, #52C41A, #95DE64)',
  'linear-gradient(135deg, #EB2F96, #F759AB)',
];

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const getInitials = (firstName: string, lastName: string): string => {
  const a = (firstName || '').trim().charAt(0);
  const b = (lastName || '').trim().charAt(0);
  return `${a}${b}`.toUpperCase() || '?';
};

const getAvatarGradient = (key: string): string => {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  }
  return AVATAR_GRADIENTS[hash % AVATAR_GRADIENTS.length];
};

const normalize = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

interface Props {
  mode: 'manage' | 'review';
}

function RequirementDetail({ mode }: Props) {
  const { t, i18n } = useTranslation(undefined, { i18n: appI18n });
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const { id: requirementId } = useParams<{ id: string }>();

  const isManage = mode === 'manage';
  const listPath = isManage
    ? '/portal-go/requirements'
    : '/portal-go/requirements-review';

  const datePart = FunctionsHelper.getDateFormatByLang(i18n.language);
  const dateTimeFormat = `${datePart} h:mm A`;

  const [detail, setDetail] = useState<RequirementDetailModel | null>(null);
  const [detailLoading, setDetailLoading] = useState<boolean>(true);
  const [associations, setAssociations] = useState<Association[]>([]);
  const [associationsLoading, setAssociationsLoading] = useState<boolean>(true);

  // Tabla
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [term, setTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<AssociationStatus[]>([]);
  const [statusFiltersOpen, setStatusFiltersOpen] = useState<boolean>(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  // Modales
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [associateModalOpen, setAssociateModalOpen] = useState<boolean>(false);
  const [removing, setRemoving] = useState<boolean>(false);

  // Modal de detalle del lead
  const [leadModalOpen, setLeadModalOpen] = useState<boolean>(false);
  const [leadModalUserId, setLeadModalUserId] = useState<string | null>(null);
  const [leadStatuses, setLeadStatuses] = useState<ProfileStatusEntry[]>([]);

  useEffect(() => {
    if (!requirementId) return;
    fetchDetail();
    fetchAssociations();
  }, [requirementId]);

  useEffect(() => {
    fetchLeadStatuses();
  }, []);

  const fetchDetail = async () => {
    if (!requirementId) return;
    setDetailLoading(true);
    const response = await RequirementService.getRequirementById(requirementId);
    if (response.success) {
      setDetail(response.data as RequirementDetailModel);
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t('scenes.requirements.detail.errors.fetch_detail'),
        3.5
      );
    }
    setDetailLoading(false);
  };

  const fetchAssociations = async (silent = false) => {
    if (!requirementId) return;
    if (!silent) setAssociationsLoading(true);
    const response = await RequirementService.getAssociations(requirementId);
    if (response.success) {
      const payload = response.data as AssociationsResponse;
      setAssociations(payload.data || []);
    } else if (!silent) {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t('scenes.requirements.detail.errors.fetch_associations'),
        3.5
      );
    }
    if (!silent) setAssociationsLoading(false);
  };

  const filtered = useMemo(() => {
    const q = normalize(term.trim());
    return associations.filter((row) => {
      if (statusFilter.length > 0 && !statusFilter.includes(row.status))
        return false;
      if (q) {
        const fullName = normalize(`${row.firstName} ${row.lastName}`);
        if (!fullName.includes(q)) return false;
      }
      return true;
    });
  }, [associations, term, statusFilter]);

  useEffect(() => {
    setPage(1);
    setSelectedRowKeys([]);
  }, [term, statusFilter]);

  const onSearch: SearchProps['onSearch'] = (value) => {
    setTerm(value);
  };

  const handleToggleStatus = () => {
    if (!detail) return;
    const newStatus: RequirementStatus =
      detail.status === 'open' ? 'closed' : 'open';
    const isClosing = newStatus === 'closed';
    const key = isClosing ? 'close' : 'open';

    modal.confirm({
      title: t(`scenes.requirements.detail.status_confirm.title_${key}`),
      icon: <ExclamationCircleOutlined />,
      content: t(`scenes.requirements.detail.status_confirm.content_${key}`, {
        name: detail.name,
      }),
      okText: t(`scenes.requirements.detail.status_confirm.ok_${key}`),
      cancelText: t('scenes.requirements.detail.status_confirm.cancel'),
      okButtonProps: { danger: isClosing },
      onOk: async () => {
        const response = await RequirementService.updateStatus(detail.id, {
          status: newStatus,
        });
        if (response.success) {
          message.success(
            t(`scenes.requirements.detail.status_confirm.success_${key}`),
            2.5
          );
          setDetail((prev) => (prev ? { ...prev, status: newStatus } : prev));
        } else {
          const error = response.data as ApiErrorData;
          message.error(
            (error.message as string) ||
              t('scenes.requirements.detail.errors.update_status'),
            3.5
          );
        }
      },
    });
  };

  const handleEditSaved = () => {
    message.success(t('scenes.requirements.scene.edit.success'), 2.5);
    fetchDetail();
  };

  const handleRemoveSelected = async () => {
    if (!requirementId) return;
    const userIds = selectedRowKeys.map((k) => String(k));
    if (userIds.length === 0) return;

    setRemoving(true);
    const response = await RequirementService.removeLeads(
      requirementId,
      userIds
    );
    setRemoving(false);

    if (response.success) {
      const data = response.data as RemoveLeadsResponse;
      const removedCount = data.removedIds?.length || 0;
      const ignoredCount = data.ignoredIds?.length || 0;
      message.success(
        t('scenes.requirements.detail.associated.removed_success', {
          count: removedCount,
        }),
        3
      );
      if (ignoredCount > 0) {
        message.info(
          t('scenes.requirements.detail.associated.ignored_note', {
            count: ignoredCount,
          }),
          3.5
        );
      }
      setSelectedRowKeys([]);
      fetchAssociations(true);
      fetchDetail();
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t('scenes.requirements.detail.errors.remove_failed'),
        3.5
      );
    }
  };

  const handleAssociated = () => {
    fetchAssociations(true);
    fetchDetail();
  };

  const fetchLeadStatuses = async () => {
    const response = await LeadService.getProfileStatuses();
    if (response.success) {
      setLeadStatuses(response.data as ProfileStatusEntry[]);
    }
  };

  const openLeadModal = (userId: string) => {
    setLeadModalUserId(userId);
    setLeadModalOpen(true);
  };

  const closeLeadModal = () => {
    setLeadModalOpen(false);
    setLeadModalUserId(null);
  };

  // Actualiza in-place los datos de la fila del lead (sin recargar la lista)
  // tras editarlo en el modal de detalle. No toca el estado de asociación.
  const handleLeadUpdated = async () => {
    if (!leadModalUserId) return;
    const response = await LeadService.getById(leadModalUserId);
    if (!response.success) return;
    const { profile, city } = response.data as AdminUserDetail;
    setAssociations((prev) =>
      prev.map((row) =>
        row.userId === leadModalUserId
          ? {
              ...row,
              firstName: profile.firstName,
              lastName: profile.lastName,
              avatarUrl: profile.avatarUrl,
              rut: profile.rut,
              cityId: profile.cityId,
              cityName: city?.cityName ?? null,
              provinceName: city?.provinceName ?? null,
              regionName: city?.regionName ?? null,
              provinceId: city?.provinceId ?? null,
              regionId: city?.regionId ?? null,
              scoreAptitude: profile.scoreAptitude,
              scoreProfile: profile.scoreProfile,
            }
          : row
      )
    );
  };

  // Actualiza el estado de las filas localmente (según los ids que el backend
  // confirmó en updatedIds), sin recargar la lista, para que no salte el orden.
  const applyLocalStatus = (
    userIds: string[],
    newStatus: AssociationStatus
  ) => {
    if (userIds.length === 0) return;
    const ids = new Set(userIds);
    const label = t(
      `scenes.requirements.detail.association_statuses.${newStatus}`
    );
    setAssociations((prev) =>
      prev.map((row) =>
        ids.has(row.userId)
          ? { ...row, status: newStatus, statusLabel: label }
          : row
      )
    );
  };

  const doAccept = async (userIds: string[]) => {
    if (!requirementId || userIds.length === 0) return;
    const response = await RequirementService.acceptLeads(
      requirementId,
      userIds
    );

    if (response.success) {
      const data = response.data as AcceptLeadsResponse;
      const updatedCount = (data.updatedIds || []).length;
      const conflictCount = (data.conflictIds || []).length;
      applyLocalStatus(data.updatedIds || [], 'accepted');
      if (updatedCount > 0) {
        message.success(
          t('scenes.requirements.detail.review.accept_success', {
            count: updatedCount,
          }),
          2.5
        );
      }
      if (conflictCount > 0) {
        message.warning(
          t('scenes.requirements.detail.review.conflict_warning', {
            count: conflictCount,
          }),
          3.5
        );
      }
      setSelectedRowKeys([]);
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t('scenes.requirements.detail.errors.fetch_associations'),
        3.5
      );
    }
  };

  const doReject = async (userIds: string[]) => {
    if (!requirementId || userIds.length === 0) return;
    const response = await RequirementService.rejectLeads(
      requirementId,
      userIds
    );

    if (response.success) {
      const data = response.data as RejectLeadsResponse;
      const updatedCount = (data.updatedIds || []).length;
      applyLocalStatus(data.updatedIds || [], 'rejected');
      message.success(
        t('scenes.requirements.detail.review.reject_success', {
          count: updatedCount,
        }),
        2.5
      );
      setSelectedRowKeys([]);
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t('scenes.requirements.detail.errors.fetch_associations'),
        3.5
      );
    }
  };

  const confirmAccept = (userIds: string[]) => {
    if (userIds.length === 0) return;
    modal.confirm({
      title: t('scenes.requirements.detail.review.accept_confirm_title'),
      icon: <ExclamationCircleOutlined />,
      content: t('scenes.requirements.detail.review.accept_confirm_content', {
        count: userIds.length,
      }),
      okText: t('scenes.requirements.detail.review.accept'),
      cancelText: t('scenes.requirements.detail.status_confirm.cancel'),
      onOk: () => doAccept(userIds),
    });
  };

  const confirmReject = (userIds: string[]) => {
    if (userIds.length === 0) return;
    modal.confirm({
      title: t('scenes.requirements.detail.review.reject_confirm_title'),
      icon: <ExclamationCircleOutlined />,
      content: t('scenes.requirements.detail.review.reject_confirm_content', {
        count: userIds.length,
      }),
      okText: t('scenes.requirements.detail.review.reject'),
      cancelText: t('scenes.requirements.detail.status_confirm.cancel'),
      okButtonProps: { danger: true },
      onOk: () => doReject(userIds),
    });
  };

  const getHeaderMenu = (isOpen: boolean): MenuProps['items'] => [
    {
      key: 'edit',
      label: (
        <span>
          <EditOutlined style={{ marginRight: 8 }} />
          {t('scenes.requirements.detail.actions.edit')}
        </span>
      ),
      onClick: () => setEditModalOpen(true),
    },
    { type: 'divider' },
    {
      key: 'toggle-status',
      label: (
        <span style={isOpen ? { color: '#FF4D4F' } : undefined}>
          {isOpen ? (
            <LockOutlined style={{ marginRight: 8 }} />
          ) : (
            <UnlockOutlined style={{ marginRight: 8 }} />
          )}
          {t(
            isOpen
              ? 'scenes.requirements.detail.actions.close'
              : 'scenes.requirements.detail.actions.open'
          )}
        </span>
      ),
      onClick: handleToggleStatus,
    },
  ];

  const STATUS_OPTIONS: AssociationStatus[] = [
    'pending_review',
    'accepted',
    'rejected',
  ];

  const toggleStatus = (s: AssociationStatus, checked: boolean) => {
    setStatusFilter((prev) =>
      checked ? [...prev, s] : prev.filter((x) => x !== s)
    );
  };

  const statusFilterContent = (
    <div className="requirement-status-filter">
      <Checkbox
        checked={statusFilter.length === 0}
        onChange={(e) => {
          if (e.target.checked) setStatusFilter([]);
        }}
      >
        {t('scenes.requirements.detail.associated.status_filter_all')}
      </Checkbox>
      <div className="requirement-status-filter-options">
        {STATUS_OPTIONS.map((s) => (
          <Checkbox
            key={s}
            checked={statusFilter.includes(s)}
            onChange={(e) => toggleStatus(s, e.target.checked)}
          >
            {t(`scenes.requirements.detail.association_statuses.${s}`)}
          </Checkbox>
        ))}
      </div>
    </div>
  );

  // Label de ciudad variable según país (Comuna/Distrito/Municipio/Ciudad).
  const cityLabel = useMemo(() => {
    const c = (detail?.countryCode || '').toUpperCase();
    const key = c === 'CL' || c === 'PE' || c === 'CO' ? c : 'default';
    return t(
      `scenes.requirements.detail.associate_modal.filters.city_labels.${key}`
    );
  }, [detail, t]);

  const formatDateCell = (iso: string): string =>
    iso ? dayjs(iso).format(dateTimeFormat) : '—';

  const renderAvatar = (row: Association) => (
    <span
      className="requirement-detail-avatar"
      style={{ background: getAvatarGradient(row.userId) }}
    >
      {row.avatarUrl ? (
        <img src={row.avatarUrl} alt="" />
      ) : (
        getInitials(row.firstName, row.lastName)
      )}
    </span>
  );

  const renderHeader = () => {
    if (detailLoading || !detail) {
      return (
        <Card className="requirement-detail-header-card">
          <div className="requirement-detail-header-top">
            <div className="requirement-detail-title-block">
              <div className="requirement-detail-title-row">
                <Skeleton.Input active style={{ width: 220, height: 28 }} />
                <Skeleton.Input active size="small" style={{ width: 150 }} />
                <Skeleton.Button active size="small" style={{ width: 72 }} />
              </div>
              <div
                className="requirement-detail-meta"
                style={{ marginTop: 16 }}
              >
                <Skeleton.Input active size="small" style={{ width: 150 }} />
                <Skeleton.Input active size="small" style={{ width: 190 }} />
                <Skeleton.Input active size="small" style={{ width: 150 }} />
              </div>
            </div>
            <div className="requirement-detail-actions">
              <Skeleton.Button active style={{ width: 32 }} />
            </div>
          </div>
        </Card>
      );
    }
    const isOpen = detail.status === 'open';

    return (
      <Card className="requirement-detail-header-card">
        <div className="requirement-detail-header-top">
          <div className="requirement-detail-title-block">
            <div className="requirement-detail-title-row">
              <span className="requirement-detail-title">{detail.name}</span>
              <span className="requirement-detail-id">{detail.id}</span>
              <Tag
                color={STATUS_COLOR[detail.status]}
                style={{ marginRight: 0 }}
              >
                {t(`scenes.requirements.scene.statuses.${detail.status}`)}
              </Tag>
            </div>
            <div className="requirement-detail-meta">
              {detail.task?.name && (
                <span className="requirement-detail-meta-item">
                  <NotificationOutlined />
                  {t('scenes.requirements.detail.campaign.title')}:{' '}
                  <strong>{detail.task.name}</strong>
                </span>
              )}
              <span className="requirement-detail-meta-item">
                <TeamOutlined />
                {t('scenes.requirements.detail.meta.capacity')}:{' '}
                <strong>
                  {detail.associatedCount} / {detail.referentialCapacity}
                </strong>
              </span>
              <span className="requirement-detail-meta-item">
                <CalendarOutlined />
                {t('scenes.requirements.detail.meta.deadline')}:{' '}
                <strong>
                  {formatDateCell(detail.leadAssociationDeadline)}
                </strong>
              </span>
            </div>
          </div>
          <div className="requirement-detail-actions">
            <Dropdown
              menu={{ items: getHeaderMenu(isOpen) }}
              placement="bottomRight"
              trigger={['click']}
            >
              <Button
                icon={<MoreOutlined />}
                aria-label={t('scenes.requirements.detail.actions.menu')}
              />
            </Dropdown>
          </div>
        </div>
      </Card>
    );
  };

  const renderAptitude = (row: Association) =>
    row.scoreAptitude ? (
      <Tag color={APTITUDE_COLOR[row.scoreAptitude]} style={{ marginRight: 0 }}>
        {row.scoreAptitude}
      </Tag>
    ) : (
      <span>—</span>
    );

  const getRowMenu = (row: Association): MenuProps['items'] => {
    const canAccept =
      row.status === 'pending_review' || row.status === 'rejected';
    const canReject =
      row.status === 'pending_review' || row.status === 'accepted';
    const items: NonNullable<MenuProps['items']> = [];
    if (canAccept) {
      items.push({
        key: 'accept',
        label: (
          <span style={{ color: '#52C41A' }}>
            <CheckOutlined style={{ marginRight: 8 }} />
            {t('scenes.requirements.detail.review.accept')}
          </span>
        ),
        onClick: () => confirmAccept([row.userId]),
      });
    }
    if (canReject) {
      items.push({
        key: 'reject',
        label: (
          <span style={{ color: '#FF4D4F' }}>
            <CloseOutlined style={{ marginRight: 8 }} />
            {t('scenes.requirements.detail.review.reject')}
          </span>
        ),
        onClick: () => confirmReject([row.userId]),
      });
    }
    return items;
  };

  return (
    <Layout title={''}>
      <div className="requirement-detail-scene">
        <Breadcrumb
          className="requirement-detail-breadcrumb"
          items={[
            {
              title: (
                <a onClick={() => navigate(listPath)}>
                  <ArrowLeftOutlined style={{ marginRight: 4 }} />
                  {t('scenes.requirements.detail.back')}
                </a>
              ),
            },
            ...(detail ? [{ title: detail.name }] : []),
          ]}
        />

        {renderHeader()}

        {detail && (
          <CapacityAlert
            associatedCount={detail.associatedCount}
            referentialCapacity={detail.referentialCapacity}
          />
        )}

        <Card>
          <Row gutter={[16, 12]} align="middle" justify="space-between">
            <Col flex="none">
              <Search
                placeholder={t(
                  'scenes.requirements.detail.associated.search_placeholder'
                )}
                onSearch={onSearch}
                allowClear
                style={{ width: 240 }}
              />
            </Col>
            <Col flex="none">
              <Space size={8} wrap>
                <Popover
                  trigger="click"
                  placement="bottomRight"
                  open={statusFiltersOpen}
                  onOpenChange={setStatusFiltersOpen}
                  content={statusFilterContent}
                  destroyTooltipOnHide
                >
                  <Badge
                    count={statusFilter.length > 0 ? 1 : 0}
                    size="small"
                    offset={[-4, 4]}
                  >
                    <Button icon={<FilterOutlined />}>
                      {t('scenes.requirements.detail.associated.filters')}
                    </Button>
                  </Badge>
                </Popover>
                {isManage && (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setAssociateModalOpen(true)}
                  >
                    {t(
                      'scenes.requirements.detail.associated.associate_button'
                    )}
                  </Button>
                )}
              </Space>
            </Col>
          </Row>
        </Card>

        {selectedRowKeys.length > 0 && (
          <div
            className="requirement-detail-bulk-bar"
            style={{ marginTop: 12 }}
          >
            <span>
              <strong>{selectedRowKeys.length}</strong>{' '}
              {t('scenes.requirements.detail.associated.selected')}
            </span>
            <div className="requirement-detail-bulk-actions">
              {isManage && (
                <Button
                  danger
                  loading={removing}
                  onClick={handleRemoveSelected}
                >
                  {t('scenes.requirements.detail.associated.remove_selected')}
                </Button>
              )}
              {!isManage && (
                <Button
                  className="requirement-accept-btn"
                  icon={<CheckOutlined />}
                  onClick={() =>
                    confirmAccept(selectedRowKeys.map((k) => String(k)))
                  }
                >
                  {t('scenes.requirements.detail.review.bulk_accept')}
                </Button>
              )}
              {!isManage && (
                <Button
                  className="requirement-reject-btn"
                  icon={<CloseOutlined />}
                  onClick={() =>
                    confirmReject(selectedRowKeys.map((k) => String(k)))
                  }
                >
                  {t('scenes.requirements.detail.review.bulk_reject')}
                </Button>
              )}
              <Button onClick={() => setSelectedRowKeys([])}>
                {t('scenes.requirements.detail.status_confirm.cancel')}
              </Button>
            </div>
          </div>
        )}

        <Card
          style={{
            marginTop: selectedRowKeys.length > 0 ? 0 : 12,
          }}
        >
          <Table<Association>
            dataSource={filtered}
            bordered
            loading={associationsLoading}
            size="small"
            scroll={{ x: !isManage ? 940 : 880 }}
            pagination={{
              current: page,
              pageSize,
              total: filtered.length,
              showSizeChanger: true,
              pageSizeOptions: PAGE_SIZE_OPTIONS.map(String),
            }}
            rowKey={(record) => record.userId}
            onChange={(pagination) => {
              if (pagination.current && pagination.current !== page) {
                setPage(pagination.current);
              }
              if (pagination.pageSize && pagination.pageSize !== pageSize) {
                setPageSize(pagination.pageSize);
                setPage(1);
              }
            }}
            rowSelection={{
              selectedRowKeys,
              onChange: (keys) => setSelectedRowKeys(keys),
            }}
          >
            <Column
              title={t('scenes.requirements.detail.associated.columns.lead')}
              key="lead"
              render={(row: Association) => (
                <div className="requirement-detail-lead-cell">
                  {renderAvatar(row)}
                  <div className="requirement-detail-lead-info">
                    <button
                      type="button"
                      className="requirement-detail-lead-link"
                      onClick={() => openLeadModal(row.userId)}
                    >
                      {row.firstName} {row.lastName}
                    </button>
                    {row.rut && (
                      <span className="requirement-detail-lead-doc">
                        {row.rut}
                      </span>
                    )}
                  </div>
                </div>
              )}
            />
            <Column
              title={cityLabel}
              key="city"
              render={(row: Association) => {
                const full = [row.cityName, row.provinceName, row.regionName]
                  .filter(Boolean)
                  .join(' · ');
                return (
                  <Tooltip title={full || undefined}>
                    <span>{row.cityName || '—'}</span>
                  </Tooltip>
                );
              }}
            />
            <Column
              width={90}
              align="center"
              title={t(
                'scenes.requirements.detail.associated.columns.aptitude'
              )}
              key="aptitude"
              render={(row: Association) => renderAptitude(row)}
            />
            <Column
              width={90}
              align="center"
              title={t('scenes.requirements.detail.associated.columns.profile')}
              key="profile"
              render={(row: Association) => (
                <span>
                  {row.scoreProfile !== null ? `${row.scoreProfile} ★` : '—'}
                </span>
              )}
            />
            <Column
              width={170}
              align="center"
              title={t('scenes.requirements.detail.associated.columns.status')}
              key="status"
              render={(row: Association) => (
                <Tag
                  color={ASSOCIATION_STATUS_COLOR[row.status]}
                  style={{ marginRight: 0 }}
                >
                  {row.statusLabel ||
                    t(
                      `scenes.requirements.detail.association_statuses.${row.status}`
                    )}
                </Tag>
              )}
            />
            {!isManage && (
              <Column
                width={70}
                align="center"
                title=""
                key="actions"
                render={(row: Association) => (
                  <Dropdown
                    menu={{ items: getRowMenu(row) }}
                    placement="bottomRight"
                    trigger={['click']}
                  >
                    <Button size="small">
                      <MoreOutlined />
                    </Button>
                  </Dropdown>
                )}
              />
            )}
          </Table>
        </Card>
      </div>

      {detail && (
        <CreateRequirementModal
          isOpen={editModalOpen}
          mode="edit"
          requirement={detail}
          handleModalCancel={() => setEditModalOpen(false)}
          onSaved={handleEditSaved}
        />
      )}

      {isManage && detail && (
        <AssociateLeadsModal
          isOpen={associateModalOpen}
          requirementId={detail.id}
          countryCode={detail.countryCode}
          leadStatuses={leadStatuses}
          handleCancel={() => setAssociateModalOpen(false)}
          onAssociated={handleAssociated}
        />
      )}

      {leadModalUserId && (
        <DetailLeadModal
          isOpen={leadModalOpen}
          leadId={leadModalUserId}
          handleModalCancel={closeLeadModal}
          onAfterUpdate={handleLeadUpdated}
          statuses={leadStatuses}
        />
      )}
    </Layout>
  );
}

export default RequirementDetail;
